import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { timeAgo } from '@/lib/format';

interface Report {
  id: number;
  target_type: string;
  target_id: number;
  reason: string;
  description: string | null;
  status: string;
  reporter_nickname: string;
  created_at: string;
}

export function AdminReportsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'reports'],
    queryFn: () => get<{ list: Report[] }>('/admin/reports'),
  });

  const processMutation = useMutation({
    mutationFn: (params: { id: number; action: 'process' | 'dismiss' }) =>
      post(`/admin/reports/${params.id}`, { action: params.action }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'reports'] }),
  });

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <h1 className="mb-6 text-2xl font-extrabold">신고 처리</h1>

      {isLoading && <Skeleton className="h-96" />}

      <div className="space-y-3">
        {data?.list?.map((r) => (
          <div
            key={r.id}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge variant="primary">{r.target_type}</Badge>
                  <span className="text-sm font-semibold">{r.reason}</span>
                </div>
                <p className="mt-2 text-sm text-[var(--color-text-sub)]">
                  {r.description || '(설명 없음)'}
                </p>
                <div className="mt-2 text-xs text-[var(--color-text-sub)]">
                  대상 ID: #{r.target_id} · 신고자: {r.reporter_nickname} · {timeAgo(r.created_at)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => processMutation.mutate({ id: r.id, action: 'process' })}
                >
                  처리
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => processMutation.mutate({ id: r.id, action: 'dismiss' })}
                >
                  기각
                </Button>
              </div>
            </div>
          </div>
        ))}
        {data?.list?.length === 0 && (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
            처리 대기 중인 신고가 없어요.
          </div>
        )}
      </div>
    </div>
  );
}
